/**
 * Edit Mode Toolbar - Sticky bar for dashboard customization.
 * Shows save/cancel buttons and widget picker when in edit mode.
 */

import { Button } from '@/components/ui/button';
import { Settings2, Save, X, Loader2, RotateCcw } from 'lucide-react';
import { WidgetPicker } from './WidgetPicker';

interface EditModeToolbarProps {
  isEditMode: boolean;
  onEnterEditMode: () => void;
  onSave: () => void;
  onCancel: () => void;
  onToggleWidget: (widgetId: string, visible: boolean) => void;
  onResetToDefaults?: () => void;
  hiddenWidgets: string[];
  isSaving: boolean;
  hasUserCustomization?: boolean;
}

export function EditModeToolbar({
  isEditMode,
  onEnterEditMode,
  onSave,
  onCancel,
  onToggleWidget,
  onResetToDefaults,
  hiddenWidgets,
  isSaving,
  hasUserCustomization,
}: EditModeToolbarProps) {
  if (!isEditMode) {
    return (
      <div className="flex justify-end gap-2 mb-4">
        {hasUserCustomization && onResetToDefaults && (
          <Button variant="ghost" size="sm" onClick={onResetToDefaults}>
            <RotateCcw className="h-4 w-4" />
            Reset to Defaults
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onEnterEditMode}>
          <Settings2 className="h-4 w-4" />
          Customize Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="sticky top-14 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b mb-4 -mx-4 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <span className="text-sm font-medium text-primary">Edit Mode</span>
          <span className="text-sm text-muted-foreground hidden sm:inline">
            — Drag widgets to reorder
          </span>
        </div>
        <div className="flex items-center gap-2">
          <WidgetPicker
            hiddenWidgets={hiddenWidgets}
            onToggleWidget={onToggleWidget}
          />
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Layout
          </Button>
        </div>
      </div>
    </div>
  );
}
